import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Col,
  Row,
  Form,
  Tabs,
  Tab,
  Image,
  InputGroup,
  Spinner,
} from "react-bootstrap";

// Importing formik & Yup
import { Formik } from "formik";
import * as Yup from "yup";

// Default profile picture
import profilePic from "assets/images/profile.jpg";

// Importing react redux
import { useDispatch } from "react-redux";

// Importing user slices
import { removeEmployeeProfilePicture } from "state/slices/user/userSlice";

// Importing auth slice
import { updateUserInfo } from "state/slices/auth/authSlice";

// Importing toast utilities
import { showSuccessToast } from "utils/toast";

const formDefaultValues = {
  profilePicture: null,
  name: "",
  phone: "",
  email: "",
  employeeId: "",
  designation: "",
  shift: "",
  team: "",
  role: "",
  dateOfJoining: "",
  dateOfBirth: "",
  cl: 0,
  totalDD: 0,
  leaves: 0,
  recordingFrequency: "",
  recordingMode: "interval",
  address: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  password: "",
};

/**
 * Normalizes form values to ensure no null values are passed to controlled inputs
 * Converts null to empty string for string fields, undefined for select fields
 */
const normalizeFormValues = (values) => {
  if (!values) return formDefaultValues;

  return {
    ...formDefaultValues,
    ...values,
    // Convert null to empty string for text inputs
    name: values.name ?? "",
    phone: values.phone ?? "",
    email: values.email ?? "",
    employeeId: values.employeeId ?? "",
    dateOfJoining: values.dateOfJoining ?? "",
    dateOfBirth: values.dateOfBirth ?? "",
    address: values.address ?? "",
    bankName: values.bankName ?? "",
    accountNumber: values.accountNumber ?? "",
    ifscCode: values.ifscCode ?? "",
    password: values.password ?? "",
    // Convert null/undefined to empty string for select fields
    designation: values.designation ?? "",
    shift: values.shift ?? "",
    team: values.team ?? "",
    role: values.role ?? "",
    // Convert null to 0 for number fields
    cl: values.cl ?? 0,
    totalDD: values.totalDD ?? 0,
    leaves: values.leaves ?? 0,
    recordingFrequency: values.recordingFrequency ?? "",
    recordingMode: values.recordingMode ?? "interval",
    // Keep profilePicture as is (can be null, File, or string)
    profilePicture: values.profilePicture ?? null,
  };
};

const EmployeeForm = ({
  formId,
  handleSubmit,
  initialValues,
  designationOptions,
  teamOptions,
  shiftOptions,
  roleOptions,
  isAllowedToUpdate = false,
  isAllowedToDelete = false,
  deleteUserProfilePicture = () => { },
}) => {
  const dispatch = useDispatch();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [hasProfilePic, setHasProfilePic] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Update the image preview when the initialValues.profilePicture changes
    if (initialValues && initialValues.profilePicture) {
      setImagePreview(initialValues.profilePicture);
      setHasProfilePic(true);
    } else {
      setSelectedFile(null);
      setImagePreview(null);
    }
  }, [initialValues]);

  useEffect(() => {
    return () => {
      // Clean up the object URL created by URL.createObjectURL
      if (selectedFile) {
        URL.revokeObjectURL(selectedFile);
      }
    };
  }, [selectedFile]);

  const handleUnselectImage = (setFieldValue) => {
    setSelectedFile(null);
    setFieldValue("profilePicture", null);
    setImagePreview(null);
    // Reset the file input value
    fileInputRef.current.value = "";
  };

  const handleFileChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setSelectedFile(file);
      setFieldValue("profilePicture", file);
      setImagePreview(URL.createObjectURL(file));
      setHasProfilePic(false);
    } else {
      setSelectedFile(null);
      setFieldValue("profilePicture", null);
      setImagePreview(null);
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleRemovingProfilePicture = async (id) => {
    try {
      await deleteUserProfilePicture(id).unwrap();

      dispatch(removeEmployeeProfilePicture(id));

      // Update user in local storage
      dispatch(
        updateUserInfo({
          field: "biometricDetails.profilePicture",
          data: null,
        })
      );

      setSelectedFile(null);
      setImagePreview(null);
      setHasProfilePic(false);

      showSuccessToast("Profile picture deleted successfully");
    } catch (err) {
      // Error toast is automatically shown by baseQuery
    }
  };

  const innerHandleSubmit = (values, { resetForm }) => {
    try {

      handleSubmit(values);

      if (values.profilePicture) {
        setHasProfilePic(true);
      }

      setSelectedFile(null);

      // Reset the file input value
      fileInputRef.current.value = "";
    } catch (error) {
      console.log(error);
    }
  };

  // Handle form validations
  const validationSchema = Yup.object().shape({
    profilePicture: Yup.mixed()
      .nullable()
      .test(
        "fileType",
        "Only (png | jpg | jpeg) files are allowed",
        (value) => {
          if (value) {
            // If the value is a URL, allow empty value
            if (typeof value === "string" && value.startsWith("/api")) {
              return true;
            } else if (value instanceof File) {
              // If the value is a file, validate its type
              const supportedFormats = ["image/jpeg", "image/png", "image/jpg"];
              return supportedFormats.includes(value.type);
            }
            // For other cases, invalid file
            return false;
          }
          return true; // Allow empty field (no image selected)
        }
      ),
    name: Yup.string().required("Name is required"),
    designation: Yup.string().required("Designation is required"),
    team: Yup.string().required("Team is required"),
    // Require Date of Joining
    dateOfJoining: Yup.date()
      .typeError("Date of joining is required")
      .required("Date of joining is required"),
    employeeId: Yup.string().required("Employee ID is required"),
    cl: Yup.number().nullable(),
    totalDD: Yup.number().nullable(),
    leaves: Yup.number().nullable(),
    recordingFrequency: Yup.number()
      .nullable()
      .transform((value, originalValue) => (
        originalValue === '' || originalValue === null || originalValue === undefined ? null : value
      ))
      .min(1, 'Minimum is 1 second')
      .max(4 * 60 * 60, 'Maximum is 4 hours (14400 seconds)'),
    recordingMode: Yup.string().oneOf(['interval', 'continuous']),
    phone: Yup.string()
      .required("Phone number is required")
      .test("isValidPhoneNumber", "Invalid phone number", (value) => {
        if (value && value !== "") {
          // Only digits allowed, and must be exactly 10 digits (no +91 or country code)
          const phoneNumber = value.trim();
          // Disallow values starting with '+' or any non-digit character (e.g. +91)
          if (/^\+/.test(phoneNumber) || /\D/.test(phoneNumber) || phoneNumber.length !== 10) {
            return false;
          }
          return true;
        }
        return true; // Allow empty field (no phone number provided)
      }),
    email: Yup.string().email("Invalid email").required("Email is required"),
    // Require Date of Birth
    dateOfBirth: Yup.date()
      .typeError("Date of birth is required")
      .required("Date of birth is required"),
    address: Yup.string(),
    bankName: Yup.string(),
    accountNumber: Yup.string(),
    ifscCode: Yup.string(),
    shift: Yup.string().required("Shift is required"),
    password: Yup.string(),
    role: Yup.string().required("Role is required"),
  });

  // Normalize initial values to prevent null values in controlled inputs
  const normalizedInitialValues = normalizeFormValues(initialValues);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={normalizedInitialValues}
      validationSchema={validationSchema}
      onSubmit={innerHandleSubmit}
      encType="multipart/form-data"
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        touched,
        errors,
        isSubmitting,
      }) => (
        <Form id={formId} noValidate onSubmit={handleSubmit}>
          <Tabs
            defaultActiveKey="profile"
            id="uncontrolled-tab-example"
            className="mb-3 d-flex flex-column flex-md-row"
            fill={true}
          >
            {/* Tab: Profile Picture */}
            <Tab eventKey="profile" title="Profile Picture">
              <Col lg={4}>
                <Image
                  id="frame"
                  src={imagePreview ? imagePreview : profilePic}
                  className="img-fluid mb-2"
                  width={200}
                  height={200}
                  alt="user profile"
                  onError={e => {
                    e.target.src = profilePic;
                  }}
                />
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  name="profilePicture"
                  id="formFile"
                  onChange={(event) => handleFileChange(event, setFieldValue)}
                  onBlur={handleBlur}
                  isInvalid={touched.profilePicture && !!errors.profilePicture}
                  accept=".png, .jpg, .jpeg"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.profilePicture}
                </Form.Control.Feedback>
                <div className="d-flex align-md-items-center flex-md-row flex-column text-nowrap">
                  <Button
                    variant="secondary"
                    className="mt-3 me-md-3"
                    onClick={() => handleUnselectImage(setFieldValue)}
                    disabled={!selectedFile ? true : false}
                  >
                    Unselect Photo
                  </Button>
                  <Button
                    variant="danger"
                    className="mt-3"
                    onClick={() => handleRemovingProfilePicture(values.id)}
                    disabled={(hasProfilePic && isAllowedToDelete) ? false : true}
                  >
                    Delete Photo
                  </Button>
                </div>
              </Col>
            </Tab>

            {/* Tab: Personal Details */}
            <Tab eventKey="personal" title="Personal Details">
              <Row>
                <Col lg={4} className="mb-4">
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Name"
                      name="name"
                      isInvalid={touched.name && !!errors.name}
                      value={values.name || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4} className="mb-4">
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Designation</Form.Label>
                    <Form.Control
                      as="select"
                      name="designation"
                      isInvalid={touched.designation && !!errors.designation}
                      value={values.designation || ""}
                      onChange={handleChange}
                    >
                      <>
                        <option value="">--- Select Position ---</option>
                        {designationOptions?.map((designation) => (
                          <option key={designation?.id} value={designation.id}>
                            {designation.name}
                          </option>
                        ))}
                      </>
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.designation}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4} className="mb-4">
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Team</Form.Label>
                    <Form.Control
                      as="select"
                      name="team"
                      isInvalid={touched.team && !!errors.team}
                      value={values.team || ""}
                      onChange={handleChange}
                    >
                      <option value="">--- Select Team ---</option>
                      {teamOptions?.map((team) => (
                        <option key={team?.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.team}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4} className="mb-4">
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Employee ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Employee ID"
                      name="employeeId"
                      disabled={!isAllowedToUpdate}
                      isInvalid={touched.employeeId && !!errors.employeeId}
                      value={values.employeeId || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.employeeId}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4} className="mb-4">
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Phone Number</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>+91</InputGroup.Text>
                      <Form.Control
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={10}
                        minLength={10}
                        placeholder="Enter Phone Number"
                        name="phone"
                        isInvalid={touched.phone && !!errors.phone}
                        value={values.phone || ""}
                        onChange={handleChange}
                      />
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4} className="mb-4">
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter Email"
                      name="email"
                      isInvalid={touched.email && !!errors.email}
                      value={values.email || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4} className="mb-4">
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Date of Joining</Form.Label>
                    <Form.Control
                      type="date"
                      name="dateOfJoining"
                      disabled={!isAllowedToUpdate}
                      isInvalid={touched.dateOfJoining && !!errors.dateOfJoining}
                      value={values.dateOfJoining || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.dateOfJoining}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4} className="mb-4">
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Date of Birth</Form.Label>
                    <Form.Control
                      type="date"
                      name="dateOfBirth"
                      isInvalid={touched.dateOfBirth && !!errors.dateOfBirth}
                      className={`form-control${errors.dateOfBirth && touched.dateOfBirth
                        ? " is-invalid"
                        : "valid"
                        }`}
                      value={values.dateOfBirth || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.dateOfBirth}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={12}>
                  <Form.Group className="form-group">
                    <Form.Label className="control-label fw-medium mb-3">Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder="Enter Address"
                      id=""
                      cols="30"
                      rows="3"
                      name="address"
                      isInvalid={touched.address && !!errors.address}
                      value={values.address || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Tab: Salary Account Details */}
            <Tab eventKey="bank" title="Salary Account Details">
              <Row>
                <Col lg={4}>
                  <Form.Group className="form-group">
                    <Form.Label className="control-label fw-medium mb-3">Bank Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Bank Name"
                      name="bankName"
                      isInvalid={touched.bankName && !!errors.bankName}
                      value={values.bankName || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.bankName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4}>
                  <Form.Group className="form-group">
                    <Form.Label className="control-label fw-medium mb-3">Account Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Account Number"
                      name="accountNumber"
                      isInvalid={touched.accountNumber && !!errors.accountNumber}
                      value={values.accountNumber || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.accountNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4}>
                  <Form.Group className="form-group">
                    <Form.Label className="control-label fw-medium mb-3">IFSC Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter IFSC Code"
                      name="ifscCode"
                      isInvalid={touched.ifscCode && !!errors.ifscCode}
                      value={values.ifscCode || ""}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.ifscCode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Tab: Shift Details */}
            <Tab eventKey="shift" title="Shift Details">
              <Row>
                <Col lg={4}>
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Choose Shift</Form.Label>
                    <Form.Control
                      as="select"
                      name="shift"
                      isInvalid={touched.shift && !!errors.shift}
                      value={values.shift || ""}
                      onChange={handleChange}
                    >
                      <option value="">--- Select Shift ---</option>
                      {shiftOptions?.map((shift) => (
                        <option key={shift?.id} value={shift.id}>
                          {shift.name}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.shift}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Tab: Additional Details */}
            <Tab
              eventKey="additional"
              title={
                formId !== "update_profile_form" || isAllowedToUpdate
                  ? "Additional Info"
                  : "Change Password"
              }
            >
              <Row>
                {(formId !== "update_profile_form" || isAllowedToUpdate) && (
                  <>
                    <Col lg={4} className="mb-4">
                      <Form.Group className="form-group required">
                        <Form.Label className="control-label fw-medium mb-3">Role</Form.Label>
                        <Form.Control
                          as="select"
                          name="role"
                          isInvalid={touched.role && !!errors.role}
                          value={values.role || ""}
                          onChange={handleChange}
                        >
                          <option value="">--- Select Role ---</option>
                          {roleOptions?.map((role) => (
                            <option key={role?.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                          {errors.role}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col lg={4} className="mb-4">
                      <Form.Group className="form-group required">
                        <Form.Label className="control-label fw-medium mb-3">
                          Total Casual Leaves
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="cl"
                          isInvalid={touched.cl && !!errors.cl}
                          value={values.cl ?? 0}
                          onChange={handleChange}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.cl}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col lg={4} className="mb-4">
                      <Form.Group className="form-group required">
                        <Form.Label className="control-label fw-medium mb-3">
                          Total Deduction Days
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="totalDD"
                          isInvalid={touched.totalDD && !!errors.totalDD}
                          value={values.totalDD ?? 0}
                          onChange={handleChange}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.totalDD}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col lg={4}>
                      <Form.Group className="form-group required">
                        <Form.Label className="control-label fw-medium mb-3">Leaves</Form.Label>
                        <Form.Control
                          type="number"
                          name="leaves"
                          isInvalid={touched.leaves && !!errors.leaves}
                          value={values.leaves ?? 0}
                          onChange={handleChange}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.leaves}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col lg={4} className="mb-4">
                      <Form.Group className="form-group">
                        <Form.Label className="control-label fw-medium mb-3">
                          Recording Mode
                        </Form.Label>
                        <Form.Control
                          as="select"
                          name="recordingMode"
                          isInvalid={touched.recordingMode && !!errors.recordingMode}
                          value={values.recordingMode || "interval"}
                          onChange={handleChange}
                          readOnly
                        >
                          <option value="interval">Interval (short clips)</option>
                          {/* <option value="continuous">Continuous (rolling parts)</option> */}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                          {errors.recordingMode}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {values.recordingMode !== 'continuous' && (
                    <Col lg={4} className="mb-4">
                      <Form.Group className="form-group">
                        <Form.Label className="control-label fw-medium mb-3">
                          Recording Frequency (seconds)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="recordingFrequency"
                          min={1}
                          max={14400}
                          placeholder="Use default if empty"
                          isInvalid={touched.recordingFrequency && !!errors.recordingFrequency}
                          value={values.recordingFrequency ?? ""}
                          onChange={handleChange}
                        />
                        <Form.Text muted>
                          Interval between screen captures. Leave empty for the default (2 hours).
                        </Form.Text>
                        <Form.Control.Feedback type="invalid">
                          {errors.recordingFrequency}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    )}

                    {values.recordingMode === 'continuous' && (
                    <Col lg={4} className="mb-4">
                      <Form.Group className="form-group">
                        <Form.Label className="control-label fw-medium mb-3">
                          Continuous recording
                        </Form.Label>
                        <Form.Text muted>
                          Records while screen is shared and uploads a new part every 3 minutes with automatic retry on failure.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    )}
                  </>
                )}

                <Col lg={4}>
                  <Form.Group className="form-group required">
                    <Form.Label className="control-label fw-medium mb-3">Password</Form.Label>
                    <InputGroup hasValidation className="overflow-hidden">
                      <Form.Control
                        type={passwordVisible ? "text" : "password"}
                        name="password"
                        id="password"
                        placeholder="Enter password"
                        value={values.password || ""}
                        onChange={handleChange}
                        isInvalid={touched.password && !!errors.password}
                        required
                        aria-describedby="passwordHelpBlock"
                      />
                      <InputGroup
                        role="button"
                        onClick={togglePasswordVisibility}
                        style={{
                          cursor: "pointer",
                          position: "absolute",
                          right: "1px",
                          top: "8px",
                          background: "#fff",
                          width: "30px",
                          zIndex: 1000,
                          textAlign: "right",
                        }}
                      >
                        {passwordVisible ? (
                          <i className="bi bi-eye-fill"></i>
                        ) : (
                          <i className="bi bi-eye-slash-fill"></i>
                        )}
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </InputGroup>
                    <Form.Text id="passwordHelpBlock" muted>
                      Min 8 chars, uppercase, lowercase, number & special char. No recent passwords.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Form>
      )}
    </Formik>
  );
};

export default EmployeeForm;
